import { useState } from 'react'
import { ChevronDown, ChevronUp, Info } from 'lucide-react'

/**
 * The forbehold panel at the top of each Kampanjer tab: "Datagrunnlag og
 * forbehold" on loans (#498), "Om leasingkampanjene og beregningene" on
 * leasing (#636). Collapsible, and open on first render.
 *
 * Open by default because a forbehold that nobody reads is not a forbehold,
 * and both issues ask for the text at the top of the page. Collapsible because
 * several paragraphs above a wide grid is real vertical space to pay on every
 * visit.
 *
 * The open/closed state is per mount and deliberately not persisted — the
 * text should be in front of the reader again next time they open the page.
 *
 * `testId` names the panel; the paragraphs are `${testId}-tekst`.
 */
export default function ForbeholdPanel({ title, paragraphs, testId }: {
  title: string
  paragraphs: string[]
  testId: string
}) {
  const [open, setOpen] = useState(true)

  return (
    <div data-testid={testId} className="card p-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex items-center gap-2 w-full text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
      >
        <Info className="w-4 h-4 shrink-0 text-primary-600 dark:text-primary-400" />
        <span className="flex-1">{title}</span>
        {open
          ? <ChevronUp className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
          : <ChevronDown className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />}
      </button>

      {open && (
        <div data-testid={`${testId}-tekst`} className="mt-3 space-y-2">
          {paragraphs.map((avsnitt) => (
            <p key={avsnitt.slice(0, 24)} className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {avsnitt}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
